// ============================================================================
// AiReportScopePanel — current defined scope ON/OFF + save destination
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import {
  normalizeAiReportScope,
  saveAiReportScopeLocal,
  saveAiReportScopeBackend,
  applyAiReportScopeToEvidence,
  DEFAULT_AI_SAMPLE_ITEM_LIMIT,
  MAX_AI_SAMPLE_ITEM_LIMIT,
  normalizeSampleItemLimit,
  hasMeaningfulAiReportScope,
} from '@process-advisor/process-miner/consultant/aiReportScope';

const PANEL_STYLE = {
  margin: '0 10px 0',
  border: '1px solid #DFE1E6',
  borderTop: 'none',
  borderRadius: '0 0 8px 8px',
  background: '#fff',
  overflow: 'hidden',
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 700, color: '#172B4D', marginBottom: 6 };
const helpStyle = { fontSize: 11, color: '#5E6C84', lineHeight: 1.4 };
const inputStyle = { border: '1px solid #DFE1E6', borderRadius: 4, padding: '7px 9px', fontSize: 12, color: '#172B4D', background: '#fff' };

const OPERATORS = [
  ['equals', 'equals'],
  ['not_equals', 'does not equal'],
  ['contains', 'contains'],
  ['not_contains', 'does not contain'],
  ['starts_with', 'starts with'],
  ['ends_with', 'ends with'],
  ['is_blank', 'is blank'],
  ['not_blank', 'is not blank'],
];

const sourceLabel = (source) => {
  if (source === 'report') return 'this report';
  if (source === 'global') return 'all-reports default';
  if (source === 'local') return 'local browser fallback';
  return 'no saved scope';
};

export default function AiReportScopePanel({
  isOpen,
  onClose,
  modelId,
  reportKey,
  reportLabel = 'AI Report',
  scope,
  scopeSource = 'none',
  onScopeChange,
  onScopeSaved,
  getTaskRows = null,
  getVariants = null,
  getProcessSteps = null,
}) {
  const [saveState, setSaveState] = useState('loaded');
  const [draftScope, setDraftScope] = useState(() => normalizeAiReportScope(scope));
  const currentScope = normalizeAiReportScope(draftScope);

  const rows = useMemo(() => {
    if (!getTaskRows) return [];
    try {
      return getTaskRows(MAX_AI_SAMPLE_ITEM_LIMIT) || [];
    } catch (_) {
      return getTaskRows() || [];
    }
  }, [getTaskRows, isOpen]);

  const variants = useMemo(() => (getVariants ? (getVariants() || []) : []), [getVariants, isOpen]);
  const steps = useMemo(() => (getProcessSteps ? (getProcessSteps() || []) : []), [getProcessSteps, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setDraftScope(normalizeAiReportScope(scope));
      setSaveState('loaded');
    }
  }, [isOpen, reportKey]);

  const fieldOptions = useMemo(() => {
    const seen = new Set();
    rows.slice(0, 100).forEach(row => Object.keys(row || {}).forEach(k => seen.add(k)));
    return Array.from(seen).filter(k => !/^duration$/i.test(k));
  }, [rows]);

  const pathOptions = useMemo(() => {
    const seen = new Map();
    steps.forEach(s => {
      const from = s.from_activity ?? s.from ?? s['From Activity'] ?? '';
      const to = s.to_activity ?? s.to ?? s['To Activity'] ?? '';
      if (!from && !to) return;
      const key = `${from}|||${to}`;
      if (!seen.has(key)) seen.set(key, { from, to, label: `${from || '*'} → ${to || '*'}`, tasks: s.tasks ?? s.items ?? '' });
    });
    return Array.from(seen.values()).slice(0, 120);
  }, [steps]);

  const preview = useMemo(() => applyAiReportScopeToEvidence({ rawSampleItems: rows, processSteps: steps, variants, scope: currentScope }).summary, [rows, steps, variants, currentScope]);

  if (!isOpen) return null;

  const updateScope = (patch) => {
    const autoEnable = patch && Object.prototype.hasOwnProperty.call(patch, 'enabled') ? patch.enabled : true;
    const next = normalizeAiReportScope({ ...currentScope, ...patch, enabled: autoEnable });
    setDraftScope(next);
    setSaveState('dirty');
  };

  const setScopeEnabled = (enabled) => {
    const next = normalizeAiReportScope({ ...currentScope, enabled });
    setDraftScope(next);
    setSaveState('dirty');
  };

  const saveScope = async (saveMode) => {
    const next = normalizeAiReportScope(currentScope);
    onScopeChange?.(next, saveMode === 'all' ? 'global' : 'report');
    saveAiReportScopeLocal(modelId, reportKey, next);
    setSaveState('saving');
    const result = await saveAiReportScopeBackend(modelId, reportKey, next, saveMode);
    if (result?.success) {
      setSaveState(saveMode === 'all' ? 'saved_all' : 'saved_report');
      onScopeSaved?.(next, saveMode === 'all' ? 'global' : 'report');
    } else {
      setSaveState('local');
      onScopeSaved?.(next, 'local');
    }
  };

  const statusText = () => {
    switch (saveState) {
      case 'saving': return 'Saving…';
      case 'dirty': return 'Unsaved changes';
      case 'saved_report': return `Saved for ${reportLabel}`;
      case 'saved_all': return 'Saved as default for all reports';
      case 'local': return 'Backend save failed — saved locally only';
      default: return `Loaded from ${sourceLabel(scopeSource)}`;
    }
  };

  const toggleVariant = (id) => {
    const sid = String(id);
    const set = new Set(currentScope.excludeVariantIds || []);
    if (set.has(sid)) set.delete(sid); else set.add(sid);
    updateScope({ excludeVariantIds: Array.from(set) });
  };

  const addFieldRule = () => {
    updateScope({ excludeFieldValues: [...currentScope.excludeFieldValues, { field: fieldOptions[0] || '', operator: 'equals', value: '' }] });
  };

  const updateFieldRule = (idx, patch) => {
    const rules = currentScope.excludeFieldValues.slice();
    rules[idx] = { ...rules[idx], ...patch };
    updateScope({ excludeFieldValues: rules });
  };

  const removeFieldRule = (idx) => {
    updateScope({ excludeFieldValues: currentScope.excludeFieldValues.filter((_, i) => i !== idx) });
  };

  const togglePath = (path) => {
    const key = `${path.from}|||${path.to}`;
    const existing = currentScope.excludePaths || [];
    const exists = existing.some(p => `${p.from}|||${p.to}` === key);
    updateScope({ excludePaths: exists ? existing.filter(p => `${p.from}|||${p.to}` !== key) : [...existing, { from: path.from, to: path.to }] });
  };

  const setWorkItemsFromText = (text) => {
    const values = text.split(/[\n,]+/).map(v => v.trim()).filter(Boolean);
    updateScope({ excludeWorkItems: values });
  };

  const hasDefinedScope = hasMeaningfulAiReportScope(currentScope);
  const enabled = currentScope.enabled === true;

  return (
    <div style={PANEL_STYLE}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: '#FAFBFC', borderBottom: '1px solid #DFE1E6' }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#172B4D' }}>🎯 AI Report Scope</div>
        <div style={{ ...helpStyle, flex: 1 }}>Controls whether the current scope is active, and where it is saved.</div>
        <div style={{ fontSize: 11, color: saveState?.startsWith('saved') ? '#00875A' : saveState === 'dirty' ? '#974F0C' : saveState === 'local' ? '#BF2600' : '#5E6C84' }}>
          {statusText()}
        </div>
        <button type="button" onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 18, cursor: 'pointer', color: '#5E6C84' }}>×</button>
      </div>

      <div style={{ padding: '14px 16px 0' }}>
        <div style={{ border: '1px solid #DFE1E6', borderRadius: 8, background: enabled ? '#E3FCEF' : '#FAFBFC', padding: 12, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#172B4D' }}>Current defined scope is {enabled ? 'ON' : 'OFF'}</div>
            <div style={helpStyle}>
              {enabled
                ? `This ${reportLabel} will use the instructions, exclusions, and sample limit shown below.`
                : `This ${reportLabel} will use the standard AI evidence sample. Saved filters/instructions remain visible but inactive.`}
              {' '}Loaded from {sourceLabel(scopeSource)}.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setScopeEnabled(!enabled)}
            style={{
              minWidth: 92,
              border: enabled ? '1px solid #00875A' : '1px solid #C1C7D0',
              background: enabled ? '#00875A' : '#FFFFFF',
              color: enabled ? '#FFFFFF' : '#172B4D',
              borderRadius: 999,
              padding: '8px 14px',
              fontSize: 12,
              fontWeight: 800,
              cursor: 'pointer',
            }}
          >
            {enabled ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      <div style={{ padding: 16, display: 'grid', gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)', gap: 18, opacity: enabled ? 1 : 0.72 }}>
        <div>
          <label style={labelStyle}>Additional report instructions</label>
          <textarea
            value={currentScope.instructions || ''}
            onChange={(e) => updateScope({ instructions: e.target.value })}
            placeholder="Example: Exclude direct Open → Resolved tickets because these are known simple cases and should not influence the report. Focus on SLA risk and rework-heavy flows."
            style={{ ...inputStyle, width: '100%', minHeight: 96, resize: 'vertical', lineHeight: 1.5 }}
          />
          <div style={{ ...helpStyle, marginTop: 6 }}>Instructions refine interpretation. Evidence filters below remove items from the AI evidence before the prompt is built when scope is ON.</div>

          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Sample items sent to AI</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="number"
                min={1}
                max={MAX_AI_SAMPLE_ITEM_LIMIT}
                step={25}
                value={currentScope.sampleItemLimit ?? DEFAULT_AI_SAMPLE_ITEM_LIMIT}
                onChange={(e) => updateScope({ sampleItemLimit: normalizeSampleItemLimit(e.target.value) })}
                onBlur={(e) => updateScope({ sampleItemLimit: normalizeSampleItemLimit(e.target.value) })}
                style={{ ...inputStyle, width: 110 }}
              />
              <div style={helpStyle}>Default {DEFAULT_AI_SAMPLE_ITEM_LIMIT}. Maximum {MAX_AI_SAMPLE_ITEM_LIMIT}. Applied after scope filters and before privacy/anonymisation.</div>
            </div>
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Exclude field values</label>
            {currentScope.excludeFieldValues.length === 0 && <div style={helpStyle}>No field-value filters.</div>}
            {currentScope.excludeFieldValues.map((rule, idx) => (
              <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 1fr 28px', gap: 6, marginBottom: 6 }}>
                <select value={rule.field} onChange={(e) => updateFieldRule(idx, { field: e.target.value })} style={inputStyle}>
                  {fieldOptions.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
                <select value={rule.operator} onChange={(e) => updateFieldRule(idx, { operator: e.target.value })} style={inputStyle}>
                  {OPERATORS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </select>
                <input value={rule.value} onChange={(e) => updateFieldRule(idx, { value: e.target.value })} disabled={rule.operator === 'is_blank' || rule.operator === 'not_blank'} style={inputStyle} />
                <button type="button" onClick={() => removeFieldRule(idx)} style={{ ...inputStyle, padding: 0, cursor: 'pointer' }}>×</button>
              </div>
            ))}
            <button type="button" onClick={addFieldRule} style={{ ...inputStyle, cursor: 'pointer', fontWeight: 700, color: '#0052CC' }}>+ Add field filter</button>
          </div>

          <div style={{ marginTop: 18 }}>
            <label style={labelStyle}>Exclude specific work items</label>
            <textarea
              value={(currentScope.excludeWorkItems || []).join('\n')}
              onChange={(e) => setWorkItemsFromText(e.target.value)}
              placeholder="One key per line, e.g. INC-1234"
              style={{ ...inputStyle, width: '100%', minHeight: 70, resize: 'vertical' }}
            />
            <div style={helpStyle}>Useful for known test tickets, migrated tickets, or one-off exceptions.</div>
          </div>
        </div>

        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 14 }}>
            <div style={{ background: '#E3FCEF', border: '1px solid #ABF5D1', borderRadius: 6, padding: 10 }}><div style={{ fontSize: 22, fontWeight: 800, color: '#006644' }}>{preview.scopedItems}</div><div style={{ fontSize: 11, color: '#006644' }}>sample items sent</div></div>
            <div style={{ background: '#FFF0EC', border: '1px solid #FFBDAD', borderRadius: 6, padding: 10 }}><div style={{ fontSize: 22, fontWeight: 800, color: '#BF2600' }}>{preview.excludedItems}</div><div style={{ fontSize: 11, color: '#BF2600' }}>sample items excluded</div></div>
            <div style={{ background: '#FFFBEA', border: '1px solid #FFE380', borderRadius: 6, padding: 10 }}><div style={{ fontSize: 22, fontWeight: 800, color: '#974F0C' }}>{preview.sampleLimitedItems || 0}</div><div style={{ fontSize: 11, color: '#974F0C' }}>not sent by limit</div></div>
            <div style={{ background: '#EEF4FF', border: '1px solid #B3D4FF', borderRadius: 6, padding: 10 }}><div style={{ fontSize: 22, fontWeight: 800, color: '#0052CC' }}>{preview.filtersApplied}</div><div style={{ fontSize: 11, color: '#0052CC' }}>scope filters</div></div>
          </div>

          <label style={labelStyle}>Exclude variants</label>
          <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #DFE1E6', borderRadius: 4, marginBottom: 16 }}>
            {(variants || []).slice(0, 80).map(v => {
              const id = String(v.variant_id ?? v.id ?? v.Variant ?? v.variant ?? '');
              const label = v.variant || v.n || v.name || `Variant ${id}`;
              const checked = currentScope.excludeVariantIds.includes(id);
              return (
                <label key={id || label} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #F4F5F7', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleVariant(id)} />
                  <span style={{ fontWeight: 700, color: '#172B4D' }}>{id}</span>
                  <span style={{ color: '#5E6C84', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                  <span style={{ marginLeft: 'auto', color: '#5E6C84' }}>{v.tasks ?? v.items ?? ''}</span>
                </label>
              );
            })}
          </div>

          <label style={labelStyle}>Exclude transition paths</label>
          <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #DFE1E6', borderRadius: 4 }}>
            {pathOptions.length === 0 && <div style={{ padding: 8, ...helpStyle }}>No transition paths available.</div>}
            {pathOptions.map(p => {
              const key = `${p.from}|||${p.to}`;
              const checked = currentScope.excludePaths.some(x => `${x.from}|||${x.to}` === key);
              return (
                <label key={key} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 8px', borderBottom: '1px solid #F4F5F7', fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={checked} onChange={() => togglePath(p)} />
                  <span style={{ color: '#172B4D' }}>{p.label}</span>
                  <span style={{ marginLeft: 'auto', color: '#5E6C84' }}>{p.tasks}</span>
                </label>
              );
            })}
          </div>
          <div style={{ ...helpStyle, marginTop: 6 }}>Path filters remove matching transition evidence. If you also need matching work items removed, use variant or field-value filters.</div>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '12px 16px', borderTop: '1px solid #DFE1E6', background: '#FAFBFC' }}>
        <div style={helpStyle}>
          Current defined scope is <strong>{enabled ? 'ON' : 'OFF'}</strong>. Saving stores this state on the process model. {hasDefinedScope ? 'Clear entries and save to nullify restrictions.' : 'No filters/instructions are currently defined.'}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button type="button" onClick={() => saveScope('report')} style={{ ...inputStyle, cursor: 'pointer', fontWeight: 700, color: '#0052CC', borderColor: '#4C9AFF' }}>Save for this report</button>
          <button type="button" onClick={() => saveScope('all')} style={{ ...inputStyle, cursor: 'pointer', fontWeight: 700, color: '#FFFFFF', background: '#0052CC', borderColor: '#0052CC' }}>Save for all reports</button>
        </div>
      </div>
    </div>
  );
}

export function AiScopeButton({ onClick, state = 'off', count = 0 }) {
  const active = state === 'report' || state === 'default' || state === 'unsaved';
  const label = state === 'report' ? 'Report' : state === 'default' ? 'Default' : state === 'unsaved' ? 'Unsaved' : 'Off';
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 4,
        border: active ? '1px solid #0052CC' : '1px solid #DFE1E6', background: active ? '#EEF4FF' : '#FFFFFF',
        color: active ? '#0052CC' : '#172B4D', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >
      <span>🎯</span> AI Scope <span style={{ fontSize: 10, fontWeight: 800, background: active ? '#DEEBFF' : '#F4F5F7', color: active ? '#0052CC' : '#42526E', borderRadius: 3, padding: '1px 5px' }}>{label}</span>
      {active && count > 0 && <span style={{ fontSize: 10, fontWeight: 800, background: '#E9F2FF', color: '#0052CC', borderRadius: 3, padding: '1px 5px' }}>{count}</span>}
    </button>
  );
}
